import { useEffect, useState } from "preact/hooks";
import { fetchKioskParameters } from "../../api/kiosksClient";
import { fetchDemand } from "../../api/demandClient";
import { ENVIRONMENTS } from "../../config";
import { activeSolution, solutionInput } from "../../state/appState";
import { solutionRegexes } from "../../utils/solutionQuery";
import type { Environment, SignalingServerResponse, SsmParameter } from "../../types";
import { Spinner } from "../../components/Spinner";
import { groupKiosks, kioskStatus, paramsForKiosk, resolveEnvironment, type KioskRow } from "./kioskBuilder";
import { KiosksTable } from "./KiosksTable";
import { KioskDetailPanel } from "./KioskDetailPanel";

export function KiosksPage() {
  const [rows, setRows] = useState<KioskRow[] | null>(null);
  const [rawParams, setRawParams] = useState<SsmParameter[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fetchedAt, setFetchedAt] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    setSelectedId(null);

    Promise.all([
      fetchKioskParameters(),
      Promise.all(
        ENVIRONMENTS.map((env) =>
          fetchDemand(env)
            .then((data) => [env, data] as const)
            .catch((err) => {
              console.error(`Error fetching demand for ${env}:`, err);
              return [env, { stacks: [], queues: [] } as SignalingServerResponse] as const;
            }),
        ),
      ),
    ])
      .then(([parameters, demandResults]) => {
        if (cancelled) return;

        const demandByEnv = new Map<Environment, SignalingServerResponse>(demandResults);
        const kiosks = groupKiosks(parameters);

        const result: KioskRow[] = [...kiosks.entries()]
          .map(([id, { persona, env, opening_hours }]) => {
            const environment = resolveEnvironment(env);
            return {
              id,
              solution: persona,
              environment,
              status: kioskStatus(id, environment ? demandByEnv.get(environment) : undefined, opening_hours),
            };
          })
          .sort((a, b) => a.id.localeCompare(b.id));

        setRawParams(parameters);
        setRows(result);
        setFetchedAt(new Date().toISOString());
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error fetching kiosks:", err);
        setError("An error occurred. Please try again.");
      });

    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  if (error) return <p>{error}</p>;
  if (!rows) return <Spinner label="Loading..." />;

  const query = activeSolution.value;
  const regexes = query ? solutionRegexes(query) : [];
  const visibleRows = regexes.length === 0 ? rows : rows.filter((r) => !!r.solution && regexes.some((re) => re.test(r.solution!)));

  const selectedKiosk = rows.find((r) => r.id === selectedId) ?? null;

  return (
    <div id="kiosksContainer">
      {query && (
        <div class="status-banner">
          <p>
            Filtered by <strong>{query}</strong>{" "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                activeSolution.value = "";
                solutionInput.value = "";
              }}
            >
              Clear
            </a>
          </p>
        </div>
      )}
      {fetchedAt && (
        <div id="fetchStatus">
          Data fetched: {fetchedAt}{" "}
          <span
            class="refresh-cache"
            title="Refresh kiosk status and SSM parameters"
            onClick={() => setRefreshKey((k) => k + 1)}
          >
            🔄
          </span>
        </div>
      )}
      <div class="kiosks-layout">
        <fieldset class="kiosks-table-wrap">
          <legend>
            <strong>Kiosks</strong>
          </legend>
          {visibleRows.length === 0 ? (
            <p>No kiosks found.</p>
          ) : (
            <KiosksTable
              rows={visibleRows}
              selectedId={selectedId}
              onSelect={(id) => setSelectedId((current) => (current === id ? null : id))}
            />
          )}
        </fieldset>
        <KioskDetailPanel
          kiosk={selectedKiosk}
          params={selectedKiosk ? paramsForKiosk(rawParams, selectedKiosk.id) : []}
        />
      </div>
    </div>
  );
}
